import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { Box } from '@mui/material'
import { useLocation, useOutletContext } from 'react-router-dom'
import { toast } from 'react-toastify'
import Intervention from './Intervention'
import Toolbar from '../../toolbar/Toolbar'
import PDFReader from '../../pdfReader/PDFReader'
import InterventionResolveDialog from '../../dialogs/InterventionResolveDialog'
import ActionMenu from '../../common/ActionMenu'
import StatusDialog from '../../dialogs/StatusDialog'
import Loading from '../../common/Loading'
import StatusField from '../../fields/StatusField'
import NotFound from '../../common/NotFound'
import AttestationPDF from './AttestationPDF'
import { useAuth } from '../../../context/AuthContext'
import useNavigate from '../../hooks/useNavigate'
import SettingsIcon from '@mui/icons-material/Settings'
import EmailIcon from '@mui/icons-material/Email'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import TransformIcon from '@mui/icons-material/Transform'
import DevicesIcon from '@mui/icons-material/Devices'
import { LiaCheckSquareSolid } from 'react-icons/lia'
import { sendDocumentEmail } from '../../../utils/emailUtils'
import {
  copyIntervention,
  createEntity,
  createInvoiceFromIntervention,
  fileToBase64,
  updateAttachments,
  updateDocumentStatus
} from '../../../utils/documentManager'
import { getObject, getObjectsWithFilter, addObject, editObjectFilter } from '../../../api/apiClientCrud'
import DocumentPreview from '../../document/DocumentPreview'
import InterventionPDF from './InterventionPDF'
import { pdf } from '@react-pdf/renderer'
import { ContentCopy } from '@mui/icons-material'

const ViewIntervention = () => {
  const [intervention, setIntervention] = useState({})
  const [company, setCompany] = useState({})
  const [img, setImg] = useState('')
  const [cachet, setCachet] = useState('')
  const [showPdf, setShowPdf] = useState(false)
  const [statusList, setStatusList] = useState([])
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false)
  const [openResolve, setOpenResolve] = useState(false)
  const [statusResolve, setStatusResolve] = useState(null)
  const [isStatusChanged, setIsStatusChanged] = useState(false)
  const [files, setFiles] = useState([])
  const [pdfUrl, setPdfUrl] = useState('')
  const [settings, setSettings] = useState({})
  const [anchorEl, setAnchorEl] = useState(null)
  const [loading, setLoading] = useState(true)

  const location = useLocation()
  const { user } = useAuth()
  const userRight = useOutletContext()
  const history = useNavigate()
  const toastId = useRef(null)

  const openMenu = useCallback((event) => setAnchorEl(event.currentTarget), [])
  const closeMenu = useCallback(() => setAnchorEl(null), [])

  const resolveNok = useCallback(() => {
    setOpenResolve(true)
    setStatusResolve(91)
    closeMenu()
  }, [closeMenu])

  const resolveOk = useCallback(() => {
    setOpenResolve(true)
    setStatusResolve(90)
    closeMenu()
  }, [closeMenu])

  const menu = useMemo(
    () => [
      { label: 'Terminée Résolue', onClick: resolveOk },
      { label: 'Terminée non résolue', onClick: resolveNok }
    ],
    [resolveOk, resolveNok]
  )

  const sendEmail = useCallback(() => {
    try {
      sendDocumentEmail({
        type: 'intervention',
        object: intervention,
        objectType: 'intervention',
        user,
        company,
        customer: intervention.customer,
        template: company.intervention_email_template,
        component: (
          <InterventionPDF
            intervention_param={intervention}
            company_param={company}
            logo_param={img}
            settings={settings}
          />
        ),
        setFiles,
        subjectPrefix: 'Votre intervention',
        statusToUpdate: 70,
        updateStatus: (newStatus) => {
          setIntervention((prev) => ({ ...prev, status: newStatus }))
          setIsStatusChanged(true)
        }
      })
    } catch (error) {
      toast.error('Erreur lors de l’envoi de l’email')
    }
  }, [intervention, user, company, img, settings])

  const closeStatusDialog = useCallback(
    (newValue) => {
      setIsStatusDialogOpen(false)
      if (newValue !== undefined && newValue !== intervention.status) {
        setIntervention((prevInter) => ({ ...prevInter, status: newValue }))
        setIsStatusChanged(true)
      }
    },
    [intervention, user, toast, setIsStatusChanged]
  )

  const material = useCallback(() => {
    try {
      sendDocumentEmail({
        type: 'attestation',
        object: intervention,
        objectType: 'intervention',
        user,
        company,
        customer: intervention.customer,
        template: company.attestation_email_template,
        component: (
          <AttestationPDF
            intervention_param={intervention}
            company_param={company}
            logo_param={img}
            user_param={user}
            cachet_param={cachet}
          />
        ),
        setFiles,
        subjectPrefix: 'Prise en charge matériel, intervention',
        statusToUpdate: 70,
        updateStatus: () => {
          setIntervention((prev) => ({ ...prev, is_attestation_material: true }))
          setIsStatusChanged(true)
        }
      })
    } catch (error) {
      toast.error('Erreur lors de l’envoi de l’attestation')
    }
  }, [intervention, user, company, img, cachet])

  const handleCloseResolve = useCallback(
    (solution) => {
      setOpenResolve(false)
      if (solution === null) return
      setIntervention((prev) => ({
        ...prev,
        status: statusResolve,
        solution
      }))
      setIsStatusChanged(true)
    },
    [statusResolve]
  )

  const transform = useCallback(async () => {
    try {
      let quote = null
      if (intervention.quote_id) quote = await getObject('quotes', intervention.quote_id)

      createEntity({
        toast,
        toastIdRef: toastId,
        history,
        user,
        entityType: 'invoices',
        settingsKey: 'invoice_number',
        contractLineName: 'Invoices',
        factoryFn: createInvoiceFromIntervention,
        args: [intervention, quote?.data],
        redirectPath: '/invoices/edit',
        successMessage: 'La facture a été créée avec succès',
        updateHistoryState: { history: '/invoices/view' },
        onSuccess: () => () => {
          setIntervention((prev) => ({ ...prev, status: 100 }))
          setIsStatusChanged(true)
        }
      })
    } catch (error) {
      toast.error('Erreur lors de la création de la facture')
    }
  }, [intervention, user, history])

  const toolbarOptions = useMemo(
    () => ({
      objectType: 'interventions',
      title: () => {
        return `Intervention ${intervention.intervention_number}`
      },
      accessRight: userRight,
      actions: {
        edit: true,
        editDisabled: () => intervention.status >= 90,
        files: true,
        back: true,
        audits: true,
        list: true
      },
      customActionsRight: [
        {
          icon: () => <PictureAsPdfOutlinedIcon />,
          tooltip: 'Afficher PDF',
          onClick: () => setShowPdf((prev) => !prev)
        },
        {
          icon: () => <EmailIcon />,
          tooltip: 'Envoyer par mail',
          onClick: sendEmail,
          disabled: () =>
            !intervention?.description ||
            !intervention?.solution ||
            intervention.status < 90 ||
            !intervention?.customer?.email
        },
        {
          icon: () => <SettingsIcon />,
          tooltip: 'Changer statut',
          onClick: () => setIsStatusDialogOpen(true),
          disabled: () => intervention.status >= 90
        },
        {
          icon: () => <ContentCopy />,
          tooltip: 'Dupliquer',
          onClick: () => duplicate(),
          disabled: () => intervention.quote_id !== null
        },
        {
          icon: () => <LiaCheckSquareSolid />,
          tooltip: 'Résoudre',
          disabled: () => intervention.status >= 90 || intervention.status === 0 || !intervention?.description,
          onClick: openMenu
        },
        {
          icon: () => <DevicesIcon />,
          tooltip: 'Envoyer attestation',
          onClick: material,
          disabled: () =>
            intervention.status >= 90 ||
            !intervention?.material ||
            intervention?.is_attestation_material ||
            !intervention?.customer?.email
        },
        {
          icon: () => <TransformIcon />,
          tooltip: 'Facturer',
          onClick: transform,
          disabled: () => intervention.status !== 90
        }
      ],
      customComponentsLeft: [
        {
          component: () => <StatusField value={intervention.status} type='interventions' statusList={statusList} />
        }
      ]
    }),
    [
      userRight,
      intervention.status,
      intervention?.description,
      intervention?.solution,
      intervention?.customer?.email,
      intervention?.material,
      intervention?.is_attestation_material,
      statusList,
      sendEmail,
      material,
      transform,
      openMenu
    ]
  )

  const duplicate = useCallback(() => {
    createEntity({
      toast,
      toastIdRef: toastId,
      getObjectsWithFilter,
      addObject,
      editObjectFilter,
      history,
      user,
      entityType: 'interventions',
      settingsKey: 'intervention_number',
      factoryFn: copyIntervention,
      args: [intervention],
      redirectPath: '/interventions/edit',
      successMessage: `L'intervention a été dupliquée avec succès`,
      updateHistoryState: { history: '/interventions/view' }
    })
  }, [toast, history, user, intervention])

  useEffect(() => {
    if (!isStatusChanged) return
    updateDocumentStatus('interventions', intervention, user, toast, setIsStatusChanged)
  }, [isStatusChanged, intervention.id, intervention, user.email])

  useEffect(() => {
    setIntervention((prev) => ({
      ...prev,
      attachments: updateAttachments(prev.attachments, files)
    }))
  }, [files])

  useEffect(() => {
    const interventionId = location?.state?.id
    if (!interventionId) {
      toast.error('Intervention introuvable.', { autoClose: 2000 })
      return
    }

    const fetchAll = async () => {
      try {
        const [statusRes, interventionRes, companyRes, settingsRes] = await Promise.all([
          getObjectsWithFilter('status', { object: 'interventions' }),
          getObject('interventions', interventionId),
          getObject('company', user.company_id),
          getObjectsWithFilter('settings', { company_id: user.company_id })
        ])

        setStatusList(statusRes.data)
        setIntervention(interventionRes.data)
        setCompany(companyRes.data)
        setSettings(settingsRes.data)

        if (interventionRes.data.attachments) {
          setFiles(interventionRes.data.attachments)
          const attach = interventionRes.data.attachments.find((f) => f.type === 'intervention')
          if (attach) {
            const blob = new Blob([new Uint8Array(attach.file.data)], { type: 'application/pdf' })
            const base64 = await fileToBase64(blob)
            setPdfUrl(`data:application/pdf;base64,${base64}`)
          }
        }

        const logoAttach = companyRes.data.attachments?.find((a) => a.type === 'logo')
        if (logoAttach) {
          const blob = new Blob([new Uint8Array(logoAttach.file.data)])
          const base64 = await fileToBase64(blob)
          setImg(base64)
        }
      } catch (error) {
        toast.error('Erreur lors du chargement des données', { autoClose: 2000 })
      } finally {
        setLoading(false)
      }
    }

    fetchAll()
  }, [location, user.company_id])

  useEffect(() => {
    if (!intervention?.id) return

    let url
    const generatePdfFromComponent = async () => {
      try {
        const attach = intervention.attachments?.find((f) => f.type === 'intervention')
        if (attach && attach.file?.data?.length) {
          const blob = new Blob([new Uint8Array(attach.file.data)], { type: 'application/pdf' })
          url = URL.createObjectURL(blob)
          setPdfUrl(url)
        } else {
          const component = (
            <InterventionPDF
              intervention_param={intervention}
              company_param={company}
              logo_param={img}
              settings={settings}
            />
          )
          const doc = pdf(component)
          const blob = await doc.toBlob()
          url = URL.createObjectURL(blob)
          setPdfUrl(url)
        }
      } catch (err) {}
    }

    generatePdfFromComponent()

    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [intervention, company, img, settings])

  if (loading)
    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          height: '100%',
          minHeight: '100px',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Loading />
      </Box>
    )
  if (!intervention?.id) return <NotFound title='Intervention introuvable' />

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexGrow: 1
      }}
    >
      <Toolbar
        options={toolbarOptions}
        object={intervention}
        setObject={setFiles}
        setParentObject={setIntervention}
        parentObject={intervention}
      />

      {showPdf ? (
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <PDFReader url={pdfUrl} filename={`intervention_${intervention?.intervention_number}.pdf`} />
        </Box>
      ) : (
        <Box sx={{ overflowY: 'auto', flexGrow: 1, paddingTop: '15px', paddingBottom: '15px' }}>
          <DocumentPreview Component={Intervention} document={intervention} />
        </Box>
      )}

      <StatusDialog
        statusList={statusList}
        open={isStatusDialogOpen}
        onClose={closeStatusDialog}
        type='interventions'
        value={intervention.status}
      />

      <InterventionResolveDialog value={intervention} open={openResolve} onClose={handleCloseResolve} />
      <ActionMenu anchorEl={anchorEl} onClose={closeMenu} menuItems={menu} />
    </Box>
  )
}

export default ViewIntervention
